import DashboardSummaryCountsWidget from '../../../../components/DashboardSummaryCountsWidget/SummaryCountWidget'
import SvgIconDrives from '../../../../assets/inlineSvg/icon-drive.svg'
import SvgIconLibraries from '../../../../assets/inlineSvg/icon-libraries.svg'
import SvgIconPolicies from '../../../../assets/inlineSvg/icon-policies.svg'
import SvgIconMembers from '../../../../assets/inlineSvg/icon-members.svg'
import { RootState } from '../../../../rootReducer'
import {
  GDriveSummary,
  GDriveSummaryQueryParams,
  OneDriveSummaryQueryParams,
  SharePointSummaryQueryParams
} from '../../../../services/api/apiTypes'
import useUrlContext from '../../../../hooks/useUrlContext'
import {
  DATA_SOURCE_ID,
  DATA_SOURCE_TYPE,
  DATA_SOURCE_TYPES,
  URL_DRIVES
} from '../../../../constants'
import { getUpdatedUrl } from '../../../../utils/urlUtil'
import { getShowLoader } from '../../../../reducers/requestReducer'
import { ACTION_SUMMARY_COUNT_POLICIES } from '../../../dashboardSummary/dashboardSummarySlice'
import { Template1SummaryCountWidgetData } from '../../renderer/utils/widgetMockdata'
import { getUniqueWidgetIndex, UNIQUE_WIDGET_INDEX } from '../../mainDashboardv2/constants'
import {
  fetchGDriveSummaryData,
  fetchOneDriveSummaryData,
  fetchSharePointSummaryData
} from '../../../summary/summarySlice'
import { getDataSourceBasePath } from '../../../../utils/dataSourceUtil'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import React, { useEffect } from 'react'

interface OwnProps {
  useMockData?: boolean
  widgetDatasourceId?: string
  widgetDatasourceType?: DATA_SOURCE_TYPES
  widgetDashboardId?: string
}

interface StateProps {
  oneDriveWidgetData: GDriveSummary
  gDriveWidgetData: GDriveSummary
  sharePointWidgetData: GDriveSummary
  policiesCount?: number
  showLoader: boolean
}

interface DispatchProps {
  fetchGDriveSummaryData: (
    filters: GDriveSummaryQueryParams & { [UNIQUE_WIDGET_INDEX]?: string }
  ) => void
  fetchOneDriveSummaryData: (
    filters: OneDriveSummaryQueryParams & { [UNIQUE_WIDGET_INDEX]?: string }
  ) => void
  fetchSharePointSummaryData: (
    filters: SharePointSummaryQueryParams & { [UNIQUE_WIDGET_INDEX]?: string }
  ) => void
}

type GdriveSummaryCountsWidgetComponentProps = StateProps & DispatchProps & OwnProps

const GdriveSummaryCountsWidget = ({
  gDriveWidgetData,
  oneDriveWidgetData,
  sharePointWidgetData,
  policiesCount,
  showLoader,
  useMockData = false,
  widgetDatasourceType,
  widgetDashboardId,
  widgetDatasourceId,
  fetchGDriveSummaryData,
  fetchOneDriveSummaryData,
  fetchSharePointSummaryData
}: GdriveSummaryCountsWidgetComponentProps) => {
  const intl = useIntl()
  const context = useUrlContext()
  const widgetIndex = getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
  const datasourceType = widgetDatasourceType
    ? widgetDatasourceType
    : (context.queryParams.datasourceType as DATA_SOURCE_TYPES)
  const isGdrive = datasourceType === DATA_SOURCE_TYPES.gdrive
  const isOneDrive = datasourceType === DATA_SOURCE_TYPES.oneDrive
  const isSharePoint = datasourceType === DATA_SOURCE_TYPES.sharePoint
  const isBox = datasourceType === DATA_SOURCE_TYPES.box
  const isDropbox = datasourceType === DATA_SOURCE_TYPES.dropbox
  const isNetDocuments = datasourceType === DATA_SOURCE_TYPES.netDocuments
  const isEgnyte = datasourceType === DATA_SOURCE_TYPES.egnyte
  const isIManage = datasourceType === DATA_SOURCE_TYPES.iManage
  const widgetInfo =
    isGdrive || isBox || isDropbox || isNetDocuments || isEgnyte || isIManage
      ? gDriveWidgetData
      : isOneDrive
      ? oneDriveWidgetData
      : sharePointWidgetData
  const { drivesCount, usersCount } = widgetInfo || {}
  const pathArr = getDataSourceBasePath(
    widgetDatasourceType,
    widgetDatasourceId,
    context.pathname,
    true
  ) as string[]
  const baseUrl = pathArr.join('/')

  useEffect(() => {
    if (useMockData) return
    if (widgetDatasourceId) {
      if (isGdrive || isBox || isDropbox || isNetDocuments || isEgnyte || isIManage) {
        fetchGDriveSummaryData({
          datasourceId: widgetDatasourceId,
          [UNIQUE_WIDGET_INDEX]: widgetIndex
        })
      } else if (isOneDrive) {
        fetchOneDriveSummaryData({
          datasourceId: widgetDatasourceId,
          [UNIQUE_WIDGET_INDEX]: widgetIndex
        })
      } else if (isSharePoint) {
        fetchSharePointSummaryData({
          datasourceId: widgetDatasourceId,
          [UNIQUE_WIDGET_INDEX]: widgetIndex
        })
      }
    }
  }, [fetchGDriveSummaryData, useMockData, widgetDatasourceId, widgetIndex])

  const widgetData = [
    {
      icon: datasourceType === DATA_SOURCE_TYPES.iManage ? <SvgIconLibraries /> : <SvgIconDrives />,
      text: intl.formatMessage({
        id: getDrivesCountTranslationId(datasourceType)
      }),
      link: getUpdatedUrl({
        pathName: baseUrl + URL_DRIVES,
        pageName: URL_DRIVES,
        queryParams: {
          ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType }),
          ...(widgetDatasourceId && { [DATA_SOURCE_ID]: widgetDatasourceId })
        }
      }),
      count: drivesCount || 0
    },
    {
      icon: <SvgIconPolicies />,
      text: intl.formatMessage({ id: 'dashboard.dataSource.widget.text.policies' }),
      count: policiesCount || 0
    }
  ]
  if (!isSharePoint && !isBox && !isNetDocuments) {
    widgetData.push({
      icon: <SvgIconMembers />,
      text: intl.formatMessage({ id: 'dashboard.dataSource.widget.text.members' }),
      count: usersCount || 0
    })
  }
  return (
    <DashboardSummaryCountsWidget
      summaryItems={widgetData}
      showLoader={showLoader}
      useMockData={useMockData}
    />
  )
}

function getDrivesCountTranslationId(datasourceType: DATA_SOURCE_TYPES) {
  switch (datasourceType) {
    case DATA_SOURCE_TYPES.dropbox:
      return 'dashboard.gdrive.widget.text.folders'
    case DATA_SOURCE_TYPES.netDocuments:
      return 'dashboard.gdrive.widget.text.cabinets'
    case DATA_SOURCE_TYPES.iManage:
      return 'table.legend.libraries'
    default:
      return 'dashboard.gdrive.widget.text.drives'
  }
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  const widgetIndex = getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
  return {
    oneDriveWidgetData: ownProps?.useMockData
      ? Template1SummaryCountWidgetData.oneDriveWidgetData
      : state.summary.widgetOneDriveSummaryData?.[widgetIndex] || {},
    gDriveWidgetData: ownProps?.useMockData
      ? Template1SummaryCountWidgetData.gDriveWidgetData
      : state.summary.widgetGDriveSummaryData?.[widgetIndex] || {},
    sharePointWidgetData: ownProps?.useMockData
      ? Template1SummaryCountWidgetData.sharePointWidgetData
      : state.summary.widgetSharePointSummaryData?.[widgetIndex] || {},
    policiesCount: ownProps?.useMockData
      ? Template1SummaryCountWidgetData.policiesCount
      : state.dashboardSummaryCounts.policiesCount?.[widgetIndex],
    showLoader: getShowLoader(ACTION_SUMMARY_COUNT_POLICIES)
  }
}

const mapDispatchToProps: DispatchProps = {
  fetchGDriveSummaryData,
  fetchOneDriveSummaryData,
  fetchSharePointSummaryData
}

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(GdriveSummaryCountsWidget)
